<script setup lang="ts">
import { computed, reactive, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useRouterItemsController } from './useRouterItemsController';
import ItpRouterItem from './ItpRouterItem.vue';
import ItpRouterContextMenu from './ItpRouterContextMenu.vue';
import { RouterItem } from './types';

type MenuAction = 'activate' | 'close' | 'closeOthers' | 'closeAll';

const router = useRouter();
const controller = useRouterItemsController();

const routerItems = computed(() => controller.state.items);
const activeItemId = computed(() => controller.state.activeId);

function navigateTo(path: string) {
  return router
    .push(path)
    .catch((error) =>
      console.error('[ItpRouterItems] navigation failed', path, error),
    );
}

function handleActivate(targetId: string) {
  const targetItem = routerItems.value.find((item) => item.id === targetId);
  if (targetItem) {
    navigateTo(targetItem.path);
  }
}

async function handleClose(targetId: string) {
  const wasActive = activeItemId.value === targetId;
  controller.close(targetId);

  if (wasActive && controller.state.activeId) {
    const nextActive = routerItems.value.find(
      (item) => item.id === controller.state.activeId,
    );

    if (nextActive) {
      await navigateTo(nextActive.path);
    }
  }
}

const dragFromItemId = ref<string | null>(null);
const dragOverItemId = ref<string | null>(null);

function handleDragStart(itemId: string) {
  dragFromItemId.value = itemId;
  dragOverItemId.value = null;
}

function handleDragOver(targetItemId: string) {
  if (targetItemId !== dragFromItemId.value) {
    dragOverItemId.value = targetItemId;
  }
}

function handleDragLeave() {
  dragOverItemId.value = null;
}

function handleDrop(droppedOnItemId: string) {
  const fromId = dragFromItemId.value;

  dragFromItemId.value = null;
  dragOverItemId.value = null;

  if (!fromId || fromId === droppedOnItemId) {
    return;
  }

  const normalIds = routerItems.value
    .filter((item) => item.closable !== false)
    .map((item) => item.id);

  const fromIndex = normalIds.indexOf(fromId);
  const toIndex = normalIds.indexOf(droppedOnItemId);
  if (fromIndex < 0 || toIndex < 0) {
    return;
  }

  controller.setIndex(fromId, toIndex);
}

function handleDragEnd() {
  dragFromItemId.value = null;
  dragOverItemId.value = null;
}

const menu = reactive({
  show: false,
  top: 0,
  left: 0,
  targetItem: null as RouterItem | null,
});

function handleContextMenu(payload: { event: MouseEvent; item: RouterItem }) {
  menu.targetItem = payload.item;
  menu.top = payload.event.clientY;
  menu.left = payload.event.clientX;
  menu.show = true;
}

function runMenuAction(action: MenuAction) {
  const item = menu.targetItem;

  if (!item && action !== 'closeAll') {
    return;
  }

  switch (action) {
    case 'activate':
      if (item) {
        handleActivate(item.id);
      }
      break;

    case 'close':
      if (item) {
        handleClose(item.id);
      }
      break;

    case 'closeOthers':
      if (item) {
        controller.closeOthers(item.id);
        const newActiveItem = controller.activeItem.value;
        if (
          newActiveItem &&
          router.currentRoute.value.fullPath !== newActiveItem.path
        ) {
          navigateTo(newActiveItem.path);
        }
      }
      break;

    case 'closeAll':
      controller.closeAll();
      const newActiveItem = controller.activeItem.value;
      if (
        newActiveItem &&
        router.currentRoute.value.fullPath !== newActiveItem.path
      ) {
        navigateTo(newActiveItem.path);
      }
      break;
  }

  menu.targetItem = null;
}
</script>

<template>
  <div class="itp-router-items">
    <ul class="list-unstyled mb-0">
      <ItpRouterItem
        v-for="routerItem in routerItems"
        :key="routerItem.id"
        :item="routerItem"
        :active="routerItem.id === activeItemId"
        :is-drag-over="dragOverItemId === routerItem.id"
        class="px-1 py-1"
        :class="{ 'bg-blue-100': routerItem.id === activeItemId }"
        @activate="handleActivate"
        @close="handleClose"
        @dragstart="handleDragStart(routerItem.id)"
        @drop="handleDrop(routerItem.id)"
        @dragend="handleDragEnd"
        @dragoveritem="handleDragOver(routerItem.id)"
        @dragleaveitem="handleDragLeave"
        @contextmenu="handleContextMenu"
      >
        <template #item="{ item, active }">
          <slot name="item" :item="item" :active="active" />
        </template>
      </ItpRouterItem>
    </ul>

    <ItpRouterContextMenu
      v-model:show="menu.show"
      :top="menu.top"
      :left="menu.left"
    >
      <li v-if="menu.targetItem">
        <button
          type="button"
          :disabled="menu.targetItem.id === activeItemId"
          @click="runMenuAction('activate')"
        >
          Activate
        </button>
      </li>
      <li v-if="menu.targetItem && menu.targetItem.closable">
        <button type="button" @click="runMenuAction('close')">Close</button>
      </li>
      <li v-if="menu.targetItem">
        <button type="button" @click="runMenuAction('closeOthers')">
          Close others
        </button>
      </li>
      <li>
        <button type="button" @click="runMenuAction('closeAll')">
          Close all
        </button>
      </li>
    </ItpRouterContextMenu>
  </div>
</template>
